import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Bath, Bed, Car, Coffee, ExternalLink, Flame, Gamepad2, Leaf, MapPin, Shield,
  ShowerHead, Sparkles, Star, Trophy, UtensilsCrossed, Waves, Wifi,
} from "lucide-react";

// Simple icon for each amenity the admin types, with a leaf for anything unmatched.
const AMENITY_ICONS = [
  [/pool table|billiard/i, Trophy],
  [/pool|swim/i, Waves],
  [/restaurant|dining|kitchen/i, UtensilsCrossed],
  [/camp\s*fire|bonfire/i, Flame],
  [/surveillance|security|cctv/i, Shield],
  [/front desk|reception/i, Coffee],
  [/housekeeping|laundry/i, ShowerHead],
  [/wifi|internet/i, Wifi],
  [/parking|car/i, Car],
  [/garden|lawn|park/i, Leaf],
  [/play|kids|children/i, Sparkles],
  [/volleyball|cricket|badminton|tennis|board games|games/i, Gamepad2],
  [/bath|spa/i, Bath],
  [/room|bed|suite/i, Bed],
];

const iconFor = (label) => (AMENITY_ICONS.find(([re]) => re.test(label)) || [null, Leaf])[1];

export default function RetreatDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/retreats/${id}`).then(({ data }) => setR(data)).catch(() => setR(false));
  }, [id]);

  if (r === false) return <main className="p-16 text-center font-display text-2xl">That property is not listed.</main>;
  if (!r) return <main className="p-16 text-center font-display text-2xl">Loading…</main>;

  const enquire = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || form.phone.trim().length < 8) {
      return toast.error("Please share your name, a valid email and phone number");
    }
    setBusy(true);
    try {
      await api.post(`/retreats/${id}/enquiry`, form);
      setDone(true);
      toast.success("Enquiry sent — the property will be in touch");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const book = () => {
    if (!r.booking_url) return toast.info("Booking link coming soon — please send an enquiry meanwhile");
    window.open(r.booking_url, "_blank", "noreferrer");
  };

  return (
    <main data-testid="retreat-detail">
      <section className="relative h-[52vh] min-h-[340px] overflow-hidden bg-primary">
        {r.images?.[0] ? (
          <img
            src={mediaUrl(r.images[0])}
            alt={r.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          // Until the partner sends their own photography, a plain warm panel — no
          // borrowed imagery standing in for the property.
          <div className="absolute inset-0 bg-primary" data-testid="retreat-hero-placeholder" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-6 lg:px-12 pb-10">
          {r.rating_text ? (
            <div className="inline-flex items-center gap-1.5 text-sm text-background bg-background/15 border border-background/25 rounded-full px-3 py-1 mb-4" data-testid="retreat-rating">
              <Star size={13} fill="currentColor" /> {r.rating_text}
            </div>
          ) : null}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-background leading-[1.05]" data-testid="retreat-name">{r.name}</h1>
          <div className="flex items-center gap-1.5 text-background/80 mt-3">
            <MapPin size={14} /> {r.city}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid lg:grid-cols-[1fr_340px] gap-14 items-start">
        <div className="min-w-0 space-y-14">
          <section>
            <div className="overline">About the property</div>
            <p className="text-lg mt-4">{r.about}</p>
            {r.description ? (
              <div className="text-muted-foreground mt-4 space-y-4">
                {r.description.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
              </div>
            ) : null}
          </section>

          {r.rooms?.length ? (
            <section data-testid="retreat-rooms">
              <div className="overline">Rooms</div>
              <h2 className="font-display text-3xl mt-2 mb-6">Where you will stay</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {r.rooms.map((room, i) => (
                  <div key={i} className="rounded-[1.5rem] border border-border overflow-hidden bg-background" data-testid={`retreat-room-${i}`}>
                    <div className="aspect-[16/10] bg-muted overflow-hidden">
                      {room.image ? (
                        <img src={mediaUrl(room.image)} alt={room.name} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Bed size={22} />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-xl">{room.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5">{room.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {r.amenities?.length ? (
            <section data-testid="retreat-amenities">
              <div className="overline">Amenities</div>
              <h2 className="font-display text-3xl mt-2 mb-6">What is on the grounds</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {r.amenities.map((a) => {
                  const Icon = iconFor(a);
                  return (
                    <div key={a} className="flex items-center gap-3 rounded-2xl border border-border px-4 py-3" data-testid={`retreat-amenity-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Icon size={15} className="text-secondary shrink-0" />
                      <span className="text-sm">{a}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {r.experiences?.length ? (
            <section data-testid="retreat-experiences">
              <div className="overline">Resort experiences</div>
              <h2 className="font-display text-3xl mt-2 mb-6">Worth staying an extra night for</h2>
              <div className="space-y-8">
                {r.experiences.map((x, i) => (
                  <div key={i} className="grid sm:grid-cols-[220px_1fr] gap-6 items-start" data-testid={`retreat-experience-${i}`}>
                    <div className="aspect-[4/3] rounded-[1.25rem] bg-muted overflow-hidden">
                      {x.image ? (
                        <img src={mediaUrl(x.image)} alt={x.title} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Sparkles size={20} /></div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl">{x.title}</h3>
                      <p className="text-muted-foreground mt-2">{x.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {r.nearby ? (
            <section data-testid="retreat-nearby">
              <div className="overline">Nearby</div>
              <h2 className="font-display text-3xl mt-2 mb-4">Around the property</h2>
              <p className="text-muted-foreground">{r.nearby}</p>
            </section>
          ) : null}
        </div>

        <aside className="lg:sticky lg:top-32 rounded-[1.75rem] border border-border bg-background p-6">
          <div className="overline">Book direct</div>
          <p className="text-sm text-muted-foreground mt-3">
            Rooms, tariffs and availability are handled by the resort on their own booking page.
          </p>
          <Button onClick={book} className="btn-action w-full mt-5" data-testid="retreat-book">
            Book Now <ExternalLink size={14} className="ml-1.5" />
          </Button>

          <div className="border-t border-border mt-7 pt-6">
            <div className="overline">Rather ask first?</div>
            {done ? (
              <p className="text-sm text-muted-foreground mt-3" data-testid="retreat-enquiry-done">
                Thank you — your enquiry is with the property. They will call or write shortly.
              </p>
            ) : (
              <form onSubmit={enquire} className="mt-3 space-y-3" data-testid="retreat-enquiry-form">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-transparent" data-testid="enquiry-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-transparent" data-testid="enquiry-phone" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-transparent" data-testid="enquiry-email" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Your question (optional)</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-transparent" data-testid="enquiry-message" />
                </div>
                <Button type="submit" variant="outline" disabled={busy} className="btn-pill border-border w-full" data-testid="enquiry-submit">
                  {busy ? "Sending…" : "Send enquiry"}
                </Button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}
